import { Litepicker } from "litepicker";
import { dueDateLabel, parseDateInput } from "../../phone_dashboard/due_date_picker";
import { toDateInput } from "../../phone_dashboard/working_days";

// The app's newer form controls (Phone Dashboard task dialog): a button that opens a menu of options,
// and a calendar button that opens Litepicker. The native <select> / text input stay in the form,
// hidden, and keep the value that is submitted.
const CARET = '<svg class="caret" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>';
const CALENDAR = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>';

const YEAR = new Date().getFullYear();

function selectLabel(select: HTMLSelectElement): string {
  return select.selectedOptions[0]?.textContent?.trim() || "—";
}

function closeAll(except?: Element): void {
  document.querySelectorAll(".cs-select.open").forEach((dd) => {
    if (dd !== except) dd.classList.remove("open");
  });
}

function enhanceSelect(select: HTMLSelectElement): void {
  if (select.dataset.csEnhanced) return;
  select.dataset.csEnhanced = "true";
  const wrap = document.createElement("div");
  wrap.className = "cs-select";
  const trigger = document.createElement("button");
  trigger.type = "button";
  trigger.className = "cs-input cs-select-trigger";
  trigger.setAttribute("aria-haspopup", "listbox");
  trigger.innerHTML = `<span class="cs-select-label"></span>${CARET}`;
  const menu = document.createElement("div");
  menu.className = "cs-select-menu";
  menu.setAttribute("role", "listbox");

  Array.from(select.options).forEach((option) => {
    const item = document.createElement("button");
    item.type = "button";
    item.className = "cs-select-opt";
    item.setAttribute("role", "option");
    item.dataset.value = option.value;
    item.disabled = option.disabled;
    item.textContent = option.textContent;
    menu.appendChild(item);
  });

  const sync = (): void => {
    trigger.querySelector(".cs-select-label")!.textContent = selectLabel(select);
    menu.querySelectorAll<HTMLElement>(".cs-select-opt").forEach((item) => {
      item.classList.toggle("active", item.dataset.value === select.value);
      item.setAttribute("aria-selected", String(item.dataset.value === select.value));
    });
  };

  trigger.addEventListener("click", () => {
    closeAll(wrap);
    wrap.classList.toggle("open");
    trigger.setAttribute("aria-expanded", String(wrap.classList.contains("open")));
  });
  menu.addEventListener("click", (e) => {
    const item = (e.target as HTMLElement).closest<HTMLButtonElement>(".cs-select-opt");
    if (!item || item.disabled) return;
    select.value = item.dataset.value || "";
    select.dispatchEvent(new Event("change", { bubbles: true }));
    sync();
    wrap.classList.remove("open");
  });

  select.addEventListener("cs:sync", sync);
  select.hidden = true;
  select.after(wrap);
  wrap.append(trigger, menu);
  sync();
}

function enhanceDate(input: HTMLInputElement): void {
  if (input.dataset.csEnhanced) return;
  input.dataset.csEnhanced = "true";
  const button = document.createElement("button");
  button.type = "button";
  button.className = "cs-input cs-date";
  button.innerHTML = `${CALENDAR}<span class="cs-date-label"></span>`;
  const sync = (): void => {
    button.querySelector(".cs-date-label")!.textContent = input.value ? dueDateLabel(input.value) : "Not set";
  };

  button.addEventListener("click", () => {
    closeAll();
    const picker = new Litepicker({
      element: input,
      singleMode: true,
      autoApply: true,
      format: "YYYY-MM-DD",
      startDate: parseDateInput(input.value) || undefined,
      dropdowns: { months: true, years: true, minYear: YEAR - 5, maxYear: YEAR + 10 }
    } as any);
    picker.on("selected", (date: any) => {
      input.value = toDateInput(date.dateInstance ? date.dateInstance : date);
      sync();
      picker.destroy();
    });
    picker.on("hide", () => picker.destroy());
    picker.show(button);
  });

  input.form?.addEventListener("reset", () => window.setTimeout(sync));
  input.hidden = true;
  input.after(button);
  sync();
}

export function enhanceFormControls(root: ParentNode): void {
  root.querySelectorAll<HTMLSelectElement>("[data-cs-enhance] select, select[data-cs-enhance]").forEach(enhanceSelect);
  root.querySelectorAll<HTMLInputElement>("[data-cs-enhance] input[data-cs-date]").forEach(enhanceDate);
}

export function bindFormControlsDismiss(): void {
  document.addEventListener("click", (e) => {
    if (!(e.target as HTMLElement).closest(".cs-select")) closeAll();
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeAll();
  });
}
